import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Input } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { FormLabel } from '../../forms/components/FormLabel/FormLabel';
import styles from './ArtistPortalCredentialsForm.module.scss';

interface ArtistPortalCredentialsFormProps {
  serviceName: string;
  isLoading: boolean;
  onSubmit: (email: string, password: string) => void;
}

/**
 * The Deezer for Creators sign-in.
 *
 * Deezer has no refresh grant, so the server keeps the sign-in to refresh the
 * numbers every night. The note below says so plainly, because an artist who
 * hands over a password deserves to read why before they do it.
 */
const ArtistPortalCredentialsFormComponent: React.FC<ArtistPortalCredentialsFormProps> = ({
  serviceName,
  isLoading,
  onSubmit,
}) => {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isIncomplete, setIncomplete] = useState(false);

  const onEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setIncomplete(false);
  }, []);

  const onPasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setIncomplete(false);
  }, []);

  const submit = useCallback(() => {
    const trimmedEmail = email.trim();
    if (trimmedEmail === '' || password === '') {
      setIncomplete(true);
      return;
    }
    onSubmit(trimmedEmail, password);
    // The password travels to the server once and is never kept here after it.
    setPassword('');
  }, [email, onSubmit, password]);

  return (
    <div>
      {isIncomplete && (
        <Alert color="danger" role="alert">
          {intl.formatMessage({
            id: 'app.sml.catalog.connect.credentials.incomplete',
            defaultMessage: 'Fill in your email and your password.',
          })}
        </Alert>
      )}
      <FormLabel forName="artist-portal-email">
        {intl.formatMessage({ id: 'app.sml.catalog.connect.credentials.email', defaultMessage: 'Email' })}
      </FormLabel>
      <Input
        id="artist-portal-email"
        type="email"
        autoComplete="username"
        value={email}
        disabled={isLoading}
        onChange={onEmailChange}
      />
      <FormLabel forName="artist-portal-password">
        {intl.formatMessage({ id: 'app.sml.catalog.connect.credentials.password', defaultMessage: 'Password' })}
      </FormLabel>
      <Input
        id="artist-portal-password"
        type="password"
        autoComplete="current-password"
        value={password}
        disabled={isLoading}
        onChange={onPasswordChange}
      />
      <p className={styles.sheetNote}>
        {intl.formatMessage(
          {
            id: 'app.sml.catalog.connect.credentials.consent',
            defaultMessage:
              'We keep your {service} sign-in, encrypted, so we can refresh your numbers every night. Disconnect deletes it.',
          },
          { service: serviceName }
        )}
      </p>
      <SMLButton color="primary" onClick={submit} disabled={isLoading}>
        {intl.formatMessage({ id: 'app.sml.catalog.connect.credentials.submit', defaultMessage: 'Sign in' })}
      </SMLButton>
    </div>
  );
};

export const ArtistPortalCredentialsForm = TypedMemo(ArtistPortalCredentialsFormComponent);
